import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { requerimientoService, orgService } from '../services';
import { useAuth } from '../context/AuthContext';
import './BandejaEnviados.css';

const BandejaEnviados = () => {
  const { esAnalista, esVpComercial, esVpOperativa } = useAuth();
  const isVpComercial = esVpComercial ? esVpComercial() : false;
  const isVpOperativa = esVpOperativa ? esVpOperativa() : false;
  const [requerimientos, setRequerimientos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [stats, setStats] = useState({ total: 0, por_estado: [], monto_total: '0' });
  const [vicepresidencias, setVicepresidencias] = useState([]);
  const [filtros, setFiltros] = useState({
    estado: '',
    vicepresidencia: '',
    fecha_desde: '',
    fecha_hasta: '',
    mes: '',
    anio: '',
  });

  // Pagination & Search state
  const [busqueda, setBusqueda] = useState('');
  const [paginaActual, setPaginaActual] = useState(1);
  const itemsPorPagina = 10;

  useEffect(() => {
    cargarVicepresidencias();
    cargarEnviados();
  }, []);

  const cargarVicepresidencias = async () => {
    try {
      const data = await orgService.getVicepresidencias();
      setVicepresidencias(data.results || data);
    } catch (error) {
      console.error('Error al cargar vicepresidencias:', error);
    }
  };

  const cargarEnviados = async () => {
    try {
      setCargando(true);
      const data = await requerimientoService.bandejaEnviados(filtros);
      setRequerimientos(data.requerimientos || []);
      setStats({
        total: data.total || 0,
        por_estado: data.por_estado || [],
        monto_total: data.monto_total || '0',
      });
    } catch (error) {
      console.error('Error al cargar enviados:', error);
    } finally {
      setCargando(false);
    }
  };

  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    setFiltros(prev => ({ ...prev, [name]: value }));
  };

  const aplicarFiltros = () => {
    cargarEnviados();
  };

  const limpiarFiltros = () => {
    setFiltros({
      estado: '',
      vicepresidencia: '',
      fecha_desde: '',
      fecha_hasta: '',
      mes: '',
      anio: '',
    });
    setTimeout(() => {
      requerimientoService.bandejaEnviados({
        estado: '',
        vicepresidencia: '',
        fecha_desde: '',
        fecha_hasta: '',
        mes: '',
        anio: '',
      }).then(data => {
        setRequerimientos(data.requerimientos || []);
        setStats({
          total: data.total || 0,
          por_estado: data.por_estado || [],
          monto_total: data.monto_total || '0',
        });
      });
    }, 100);
  };

  const getEstadoColor = (estado) => {
    const colores = {
      elaborado: '#2196f3',
      por_revision: '#ff9800',
      aprobado_vicepresidencia: '#4caf50',
      rechazado_vicepresidencia: '#f44336',
      recibido_administracion: '#00bcd4',
      aprobado_administracion: '#8bc34a',
      rechazado_administracion: '#f44336',
      pagado_parcial: '#7c3aed',
      pagado: '#9c27b0',
    };
    return colores[estado] || '#9e9e9e';
  };

  const getEstadoLabel = (estado) => {
    const labels = {
      elaborado: 'Elaborado',
      por_revision: 'Por Revisión',
      aprobado_vicepresidencia: 'Aprobado por VP',
      rechazado_vicepresidencia: 'Rechazado por VP',
      recibido_administracion: 'Recibido por Administración',
      aprobado_administracion: 'Aprobado por Administración',
      rechazado_administracion: 'Rechazado por Administración',
      pagado_parcial: 'Pagado Parcial',
      pagado: 'Pagado',
    };
    return labels[estado] || estado;
  };

  const getCriticidadBadge = (criticidad) => {
    const badges = {
      alta: { label: 'Alta 🔴', color: '#fff5f5', border: '#feb2b2', text: '#c53030' },
      media: { label: 'Media 🟡', color: '#fefcbf', border: '#faf089', text: '#b7791f' },
      baja: { label: 'Baja 🟢', color: '#f0fff4', border: '#9ae6b4', text: '#276749' }
    };
    const b = badges[criticidad];
    if (!b) return <span style={{ color: '#a0aec0', fontSize: '12px' }}>Pendiente</span>;
    return (
      <span style={{
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: 'bold',
        backgroundColor: b.color,
        border: `1px solid ${b.border}`,
        color: b.text,
        whiteSpace: 'nowrap',
        display: 'inline-block'
      }}>
        {b.label}
      </span>
    );
  };

  const meses = [
    '', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const anios = [
    '', '2024', '2025', '2026', '2027', '2028'
  ];

  const filtrados = requerimientos.filter(req => {
    const texto = `${req.correlativo || req.id} ${req.solicitante_nombre} ${req.solicitante_apellido} ${getEstadoLabel(req.estado_actual)}`.toLowerCase();
    return texto.includes(busqueda.toLowerCase());
  });

  return (
    <div className="bandeja-enviados">
      <div className="enviados-header">
        <h2>📤 Bandeja de Enviados</h2>
        <p className="subtitle">Historial de requerimientos elaborados por usted o su área</p>
      </div>

      {/* Estadísticas */}
      <div className="stats-grid">
        <div className="stat-card">
          <h3>Total Requerimientos</h3>
          <p className="stat-number">{stats.total}</p>
        </div>
        <div className="stat-card">
          <h3>Monto Total (Bs.)</h3>
          <p className="stat-number">{parseFloat(stats.monto_total).toLocaleString('es', { minimumFractionDigits: 2 })} Bs.</p>
        </div>
        {stats.por_estado.map(item => (
          <div key={item.estado_actual} className="stat-card stat-estado">
            <span
              className="estado-dot"
              style={{ backgroundColor: getEstadoColor(item.estado_actual) }}
            />
            <h3>{getEstadoLabel(item.estado_actual)}</h3>
            <p className="stat-number">{item.cantidad}</p>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div className="filtros-container">
        <h3>🔍 Búsqueda y Filtros</h3>
        <div className="filtros-grid">
          {!esAnalista() && (
            <>
              <div className="form-group">
                <label>Estatus</label>
                <select name="estado" value={filtros.estado} onChange={handleFiltroChange}>
                  <option value="">Todos</option>
                  <option value="elaborado">Elaborado</option>
                  <option value="por_revision">Por Revisión</option>
                  <option value="aprobado_vicepresidencia">Aprobado por VP</option>
                  <option value="rechazado_vicepresidencia">Rechazado por VP</option>
                  <option value="recibido_administracion">Recibido por Administración</option>
                  <option value="aprobado_administracion">Aprobado por Administración</option>
                  <option value="rechazado_administracion">Rechazado por Administración</option>
                  <option value="pagado_parcial">Pagado Parcial</option>
                  <option value="pagado">Pagado</option>
                </select>
              </div>

              <div className="form-group">
                <label>Vicepresidencia Origen</label>
                <select name="vicepresidencia" value={filtros.vicepresidencia} onChange={handleFiltroChange}>
                  <option value="">Todas</option>
                  {vicepresidencias.map(vp => (
                    <option key={vp.id} value={vp.id}>{vp.nombre} ({vp.abreviatura})</option>
                  ))}
                </select>
              </div>
            </>
          )}

          <div className="form-group">
            <label>Mes</label>
            <select name="mes" value={filtros.mes} onChange={handleFiltroChange}>
              <option value="">Todos</option>
              {meses.map((mes, i) => i > 0 && <option key={i} value={i}>{mes}</option>)}
            </select>
          </div>

          <div className="form-group">
            <label>Año</label>
            <select name="anio" value={filtros.anio} onChange={handleFiltroChange}>
              <option value="">Todos</option>
              {anios.map(anio => anio && <option key={anio} value={anio}>{anio}</option>)}
            </select>
          </div>

          <div className="form-group">
            <label>Fecha Desde</label>
            <input type="date" name="fecha_desde" value={filtros.fecha_desde} onChange={handleFiltroChange} />
          </div>

          <div className="form-group">
            <label>Fecha Hasta</label>
            <input type="date" name="fecha_hasta" value={filtros.fecha_hasta} onChange={handleFiltroChange} />
          </div>
        </div>

        <div className="filtros-actions" style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
          <button className="btn btn-primary" style={{ width: '120px' }} onClick={limpiarFiltros}>Limpiar</button>
          <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn btn-primary" style={{ width: '250px' }} onClick={aplicarFiltros}>Aplicar Filtros</button>
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div className="table-card-container">
        <div className="table-card-header">
          <h3>Listado de Requerimientos</h3>
          <span className="table-count-pill">{filtrados.length} EN TOTAL</span>
        </div>

        {requerimientos.length > 0 && (
          <div className="table-search-bar">
            <input
              type="text"
              placeholder="🔍 Buscar por código, solicitante, o estatus..."
              value={busqueda}
              onChange={(e) => {
                setBusqueda(e.target.value);
                setPaginaActual(1);
              }}
              className="table-search-input"
            />
          </div>
        )}
        
        {filtrados.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <p className="empty-message">No se encontraron requerimientos con los filtros seleccionados o coincidencias de búsqueda.</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Identificador</th>
                  <th>Solicitante</th>
                  <th>VP Emisora</th>
                  <th>Gerencia</th>
                  <th>Monto (Bs.)</th>
                  <th>Criticidad</th>
                  <th>Estatus</th>
                  <th>Fecha Creación</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const indiceUltimo = paginaActual * itemsPorPagina;
                  const indicePrimero = indiceUltimo - itemsPorPagina;
                  const reqsPaginados = filtrados.slice(indicePrimero, indiceUltimo);

                  return reqsPaginados.map(req => (
                    <tr key={req.id}>
                      <td><strong>{req.correlativo}</strong></td>
                      <td>{req.solicitante_nombre} {req.solicitante_apellido}</td>
                      <td>{req.vicepresidencia_nombre}</td>
                      <td>{req.gerencia_nombre}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{parseFloat(req.monto_bs).toLocaleString('es', { minimumFractionDigits: 2 })} Bs.</td>
                      <td>{getCriticidadBadge(req.criticidad)}</td>
                      <td>
                        <span
                          className="estado-badge"
                          style={{ backgroundColor: getEstadoColor(req.estado_actual), color: '#ffffff' }}
                        >
                          {req.estado_display || getEstadoLabel(req.estado_actual)}
                        </span>
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>{new Date(req.fecha_creacion).toLocaleDateString('es')}</td>
                      <td>
                        <Link to={`/requerimientos/${req.correlativo || req.id}`} className="btn-link">
                          Ver Detalles
                        </Link>
                      </td>
                    </tr>
                  ));
                })()}
              </tbody>
            </table>
          </div>
        )}

        {filtrados.length > 0 && (
          <div className="table-pagination-bar">
            <button 
              onClick={() => setPaginaActual(prev => Math.max(prev - 1, 1))}
              disabled={paginaActual === 1}
              className="table-pagination-button"
            >
              Anterior
            </button>
            <span className="table-pagination-info">
              Página {paginaActual}
            </span>
            <button 
              onClick={() => setPaginaActual(prev => prev + 1)}
              disabled={paginaActual * itemsPorPagina >= filtrados.length}
              className="table-pagination-button"
            >
              Siguiente
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BandejaEnviados;
